import React from "react";
import { Link2, X, Copy, ExternalLink } from "lucide-react";
import { useToast } from "../../hooks/useToast";

interface DepositLinkPopupProps {
  isOpen: boolean;
  link: string | null;
  amount?: number | null;
  currency?: string | null;
  onClose: () => void;
}

const DepositLinkPopup: React.FC<DepositLinkPopupProps> = ({
  isOpen,
  link,
  amount,
  currency,
  onClose,
}) => {
  const toast = useToast();

  if (!isOpen || !link) return null;

  const displayAmount =
    amount != null && !Number.isNaN(Number(amount))
      ? `${(currency || "").toUpperCase()} ${Number(amount).toFixed(2)}`.trim()
      : null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Short link copied.");
    } catch {
      toast.error("Could not copy link.");
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <Link2 size={16} />
            </div>
            <div>
              <h3 className="text-base font-bold text-[#402B69]">
                Deposit link
              </h3>
              {displayAmount ? (
                <p className="text-[11px] text-gray-500 font-medium">
                  Amount due: {displayAmount}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-center">
            <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 mb-1">
              Short link
            </p>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-bold text-emerald-800 break-all hover:underline"
            >
              {link}
            </a>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="h-11 rounded-xl border border-emerald-200 bg-white text-emerald-700 text-sm font-bold flex items-center justify-center gap-2 hover:bg-emerald-50"
            >
              <Copy size={15} />
              Copy
            </button>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="h-11 rounded-xl bg-emerald-600 text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-emerald-700"
            >
              <ExternalLink size={15} />
              Open
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositLinkPopup;
